<template>
  <q-toggle
    v-model="model"
    class="toggle-standard"
    :class="classes"
    :name="name"
    :label="label"
    :left-label="leftLabel"
    :val="val"
    :true-value="trueValue"
    :false-value="falseValue"
    :indeterminate-value="indeterminateValue"
    :toggle-order="toggleOrder"
    :toggle-indeterminate="toggleIndeterminate"
    :keep-color="keepColor"
    :icon="icon"
    :checked-icon="checkedIcon"
    :unchecked-icon="uncheckedIcon"
    :indeterminate-icon="indeterminateIcon"
    :icon-color="iconColor"
    :size="size"
    :tabindex="tabindex"
    :disable="isDisabled"
    :style="{ fontSize: 'initial' }"
  >
    <template
      v-for="(slot, slotName) in $slots"
      #[slotName]="scope"
      :key="slotName"
    >
      <slot
        :name="slotName"
        v-bind="scope || {}"
      />
    </template>
  </q-toggle>
</template>

<script setup>
import { computed } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Toggle' })
const model = defineModel({ type: [Boolean, Array, String], default: false })
const props = defineProps({
  val: { type: [Number, String, Boolean], default: null },
  trueValue: { type: [Number, String, Boolean], default: true },
  falseValue: { type: [Number, String, Boolean], default: false },
  indeterminateValue: { type: [Number, String], default: null },
  name: { type: String, default: null },
  toggleOrder: { type: String, default: 'tf', validator: v => ['tf', 'ft'].includes(v) },
  toggleIndeterminate: { type: Boolean, default: false },
  keepColor: { type: Boolean, default: false },
  tabindex: { type: [Number, String], default: null },
  icon: { type: String, default: null },
  checkedIcon: { type: String, default: null },
  uncheckedIcon: { type: String, default: null },
  indeterminateIcon: { type: String, default: null },
  label: { type: String, default: null },
  leftLabel: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'md',
    validate: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  color: {
    type: String,
    default: 'white',
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert'
    ].includes(v)
  },
  iconColor: {
    type: String,
    default: 'primary',
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert'
    ].includes(v)
  },
  isDisabled: { type: Boolean, default: false }
})

const classes = computed(() => cx(
  `size-${props.size}`,
  `color-${props.color}`,
  `icon-color-${props.color}`,
  props.leftLabel && 'is-left-label',
  props.isDense && 'is-dense',
  props.isDisabled && 'is-disabled',
  props.keepColor && 'keep-color'
))
</script>

<style>
@reference "#app-styles.css";

body.desktop {
  .toggle-standard:not(.disabled) {
    .q-toggle__thumb::before {
      @apply content-[''] absolute inset-0 transform scale-0 ;
      @apply bg-current opacity-12 rounded-full;
      @apply transition-[transform] duration-300 ease-out;
    }
    &:focus,
    &:hover {
      .q-toggle__thumb::before { @apply scale-0; }
    }
  }
  .q-toggle--dense:not(.disabled) {
    &:focus,
    &:hover {
      .q-toggle__thumb::before { @apply scale-0; }
    }
  }
}
</style>

<style scoped>
@reference "#app-styles.css";

.toggle-standard {
  @apply align-middle shrink-0 min-w-max min-h-max;

  &.q-toggle__native { @apply w-px h-px; }
  &:deep(.q-toggle__track) { @apply w-full h-full rounded-full opacity-50 bg-current; }

  &:deep(.q-toggle__thumb) {
    @apply top-1/2 left-0.5 transform -translate-y-1/2;
    @apply w-auto h-auto select-none z-0;
    @apply transition-[left,transform] duration-220 ease-out;

    &::after { @apply content-[''] absolute inset-0 rounded-full bg-white shadow-none }
    .q-icon { @apply text-[0.3em] min-w-[1em] text-black opacity-54 z-1; }
  }
  &:deep(.q-toggle__inner) {
    @apply px-0 py-0;
    &.q-toggle__inner--indet .q-toggle__thumb { @apply left-[0.45em] }
    &.q-toggle__inner--truthy {
      @apply text-(--primary-mid);
      .q-toggle__track { @apply opacity-100; }
      .q-toggle__thumb {
        &:after { @apply bg-white; }
        .q-icon { @apply text-white opacity-100; }
      }
    }
  }
  &.disabled { @apply opacity-75!; }

  &.size-xs {
    &:deep(.q-toggle__inner) {
      @apply text-[10px] leading-3 w-4.75 min-w-4.75 h-3;
      .q-toggle__thumb { @apply w-2 h-2; }
    }
    &:deep(.q-toggle__inner--truthy) {
      .q-toggle__thumb { @apply left-[calc(100%-10px)]; }
    }
  }
  &.size-sm {
    &:deep(.q-toggle__inner) {
      @apply text-[12px] leading-3.5 w-5.75 min-w-5.75 h-3.5;
      .q-toggle__thumb { @apply w-2.5 h-2.5; }
    }
    &:deep(.q-toggle__inner--truthy) {
      .q-toggle__thumb { @apply left-[calc(100%-12px)]; }
    }
  }
  &.size-md {
    &:deep(.q-toggle__inner) {
      @apply text-[14px] leading-4 w-6.75 min-w-6.75 h-4;
      .q-toggle__thumb { @apply w-3 h-3; }
    }
    &:deep(.q-toggle__inner--truthy) {
      .q-toggle__thumb { @apply left-[calc(100%-14px)]; }
    }
  }
  &.size-lg {
    &:deep(.q-toggle__inner) {
      @apply text-[14px] leading-4 w-7.75 min-w-7.75 h-4.5;
      .q-toggle__thumb { @apply w-3.5 h-3.5; }
    }
    &:deep(.q-toggle__inner--truthy) {
      .q-toggle__thumb { @apply left-[calc(100%-16px)]; }
    }
  }
  &.size-xl {
    &:deep(.q-toggle__inner) {
      @apply text-[14px] leading-4 w-8.75 min-w-8.75 h-5;
      .q-toggle__thumb { @apply w-4 h-4; }
    }
    &:deep(.q-toggle__inner--truthy) {
      .q-toggle__thumb { @apply left-[calc(100%-18px)]; }
    }
  }

  &.color-primary :deep(.q-toggle__inner) { @apply text-primary-mid; }
  &.color-secondary :deep(.q-toggle__inner) { @apply text-secondary-mid; }
  &.color-positive :deep(.q-toggle__inner) { @apply text-positive-mid; }
  &.color-negative :deep(.q-toggle__inner) { @apply text-negative-mid; }
  &.color-warning :deep(.q-toggle__inner) { @apply text-warning-mid; }
  &.color-info :deep(.q-toggle__inner) { @apply text-info-mid; }
  &.color-accent :deep(.q-toggle__inner) { @apply text-accent-mid; }
  &.color-invert :deep(.q-toggle__inner) { @apply text-info-mid; }
  &.color-white :deep(.q-toggle__inner) { @apply text-info-mid; }
  &.color-black :deep(.q-toggle__inner) { @apply text-info-mid; }

  &:not(.keep-color) :deep(.q-toggle__inner--falsy .q-toggle__track) { @apply bg-secondary-faint; }
}
</style>
